import { Injectable } from '@angular/core';
import * as wizeMobileApi from '@wize-mobile-api';
import { firstValueFrom, from, Observable, of } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import { EDocumentsMapper } from './e-documents.mapper';
import { EDocument, EDocumentsCategory, EDocumentsMedia } from '@qvs/core';
import * as fromActions from './slice';
import { selectEDocumentById } from './slice';
import { FileOpener } from '@capacitor-community/file-opener';
import { CACHE_FOLDERS, FileCacheService } from '~/shared/services/file-cache.service';
import { AuthService, selectIsAuthenticated } from '@qvs/core/auth';
import { environment } from '#environment';

@Injectable({
  providedIn: 'root'
})
export class EDocumentsService {
  eDocumentEnabled = environment.featureFlags.eDocumentEnabled;
  private readonly cacheDir = CACHE_FOLDERS.E_DOCUMENTS;
  private readonly eDocumentJwtScope = 'apiMobileEdocument';

  constructor(
    private readonly store: Store,
    private readonly wizeService: wizeMobileApi.EDocumentMobileService,
    private readonly fileCacheService: FileCacheService,
    private readonly authService: AuthService
  ) {}

  public loadEDocumentsCategories = (): Observable<EDocumentsCategory[]> =>
    this.wizeService
      .listCategories()
      .pipe(switchMap((wizeEDocumentsCategories) => of(EDocumentsMapper.fromWizeEDocumentsCategories(wizeEDocumentsCategories))));

  public loadEDocumentsForTimePeriod = (beginDate: string, endDate: string): Observable<EDocument[]> =>
    this.wizeService
      .listDocuments(undefined, undefined, undefined, undefined, beginDate, endDate)
      .pipe(switchMap((wizeEDocuments) => of(EDocumentsMapper.fromWizeEDocuments(wizeEDocuments))));

  public loadEDocumentsMediaAsJson = (documentId: string): Observable<EDocumentsMedia> =>
    this.wizeService
      .getEdocumentMediaDetails(documentId)
      .pipe(switchMap((wizeEDocumentsMediaJson) => of(EDocumentsMapper.fromWizeEDocumentsMediaJson(wizeEDocumentsMediaJson))));

  public init(): Promise<void> {
    this.store
      .select(selectIsAuthenticated)
      .pipe(filter((isAuthenticated) => isAuthenticated))
      .subscribe(() => {
        if (this.isEDocumentAuthorized()) {
          this.store.dispatch(fromActions.loadAllEDocuments());
          this.store.dispatch(fromActions.loadEDocumentsCategories());
        }
      });
    return Promise.resolve();
  }

  sanitizeFileName(fileName: string): string {
    return fileName.replaceAll(/[^a-zA-Z0-9._-]/g, '');
  }

  async openPdf(eDocument: EDocument): Promise<void> {
    this.markIdAsDownloaded(eDocument.id);
    if (!eDocument.media) {
      this.store.dispatch(fromActions.loadEDocumentsMediaAsJson({ documentId: eDocument.id }));
      eDocument = await firstValueFrom(
        this.store.pipe(
          select(selectEDocumentById(eDocument.id)),
          filter((eDocumentWithMedia) => !!eDocumentWithMedia?.media?.base64),
          take(1)
        )
      );
    }
    const fileName = this.sanitizeFileName(eDocument.media.name);
    if (!(await this.fileCacheService.isFileInCache(fileName, this.cacheDir))) {
      await firstValueFrom(from(this.fileCacheService.writeFile(fileName, this.cacheDir, eDocument.media.base64)));
    }
    return FileOpener.open({
      filePath: `${await this.fileCacheService.getCacheDirectory(this.cacheDir)}/${fileName}`,
      contentType: 'application/pdf'
    });
  }

  async deletePdf(eDocument: EDocument): Promise<void> {
    const fileName = this.sanitizeFileName(eDocument?.media?.name);
    return this.fileCacheService.deleteFile(fileName, this.cacheDir);
  }

  isDownloaded(id: string): boolean {
    return this.fileCacheService.isDownloaded(this.cacheDir, id);
  }

  isEDocumentAuthorized(): boolean {
    return this.eDocumentEnabled && this.authService.isScopeAuthorized(this.eDocumentJwtScope);
  }

  private markIdAsDownloaded(id: string): void {
    this.fileCacheService.markAsDownloaded(this.cacheDir, id);
  }
}
